import { SearchOutlined } from "@ant-design/icons";
import { Card, Empty, Input, List, Spin, Typography } from "antd";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import HistoryTimeline from "../../components/history/HistoryTimeline";
import { IPartNumberDtoResponse } from "../../models/Warehouse";
import { useGetHistoryByPartNumberId } from "../../query/HistoryQuery";
import { warehouseService } from "../../services/warehouseService";
import { notify } from "../../utils/toast";

const PartNumberHistory: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPart, setSelectedPart] = useState<
    IPartNumberDtoResponse[] | null
  >(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce(async (value: string) => {
      setSelectedId(null);
      const trimValue = value.trim();
      if (!trimValue) {
        setSelectedPart(null);
        return;
      }
      try {
        setIsLoading(true);
        const response = await warehouseService.getWarehouseByPartNumber(
          trimValue
        );
        setSelectedPart(response);
      } catch (error: any) {
        notify.error("Lỗi khi tìm kiếm Part Number");
        setSelectedPart(null);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );
  const { data: historyData, isLoading: isLoadingHistory } =
    useGetHistoryByPartNumberId(selectedId as number);

  return (
    <div className="flex flex-col lg:flex-row gap-4 p-4">
      <div className="w-full lg:w-[350px] xl:w-[750px]">
        <Card size="small" className="mb-4">
          <Input
            placeholder="Tìm kiếm theo mã/tên"
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full"
          />
        </Card>

        <Card
          title={
            <Typography.Text strong>Danh sách Part Number</Typography.Text>
          }
          size="small"
          className="h-[calc(100vh-200px)] overflow-auto"
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Spin />
            </div>
          ) : (
            <List
              dataSource={selectedPart || []}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className={`cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 transition-all p-3  ${
                    historyData?.partNumber?.id === item.id
                      ? "border-l-4 border-blue-500 bg-blue-50 dark:bg-gray-700"
                      : ""
                  }`}
                >
                  <div className="flex flex-col w-full gap-2 ml-2">
                    <div className="flex gap-1">
                      <Typography.Text type="secondary">Mã PN:</Typography.Text>
                      <Typography.Text strong>
                        {item.partNumber}
                      </Typography.Text>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">
                          Dự án:
                        </Typography.Text>
                        <Typography.Text>{item.project}</Typography.Text>
                      </div>

                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">AWB:</Typography.Text>
                        <Typography.Text>
                          {item.awb || "Chưa cập nhật"}
                        </Typography.Text>
                      </div>

                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">
                          Vị trí:
                        </Typography.Text>
                        <Typography.Text>{item.location.code}</Typography.Text>
                      </div>

                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">
                          Số lượng tồn:
                        </Typography.Text>
                        <Typography.Text>{item.stock || 0}</Typography.Text>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
              locale={{
                emptyText: (
                  <Empty
                    description="Không tìm thấy kết quả"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          )}
        </Card>
      </div>

      <div className="flex-1">
        {isLoadingHistory ? (
          <div className="flex justify-center items-center h-full">
            <Spin />
          </div>
        ) : selectedId ? (
          <HistoryTimeline
            title={
              historyData
                ? `Lịch sử ${historyData.partNumber.partNumber}`
                : "Lịch sử Part Number"
            }
            data={historyData?.history || []}
            isLoading={isLoadingHistory}
            isEmpty={!historyData?.history || historyData.history.length === 0}
            emptyMessage="Không có lịch sử"
          />
        ) : (
          <Card className="h-[calc(100vh-126px)] overflow-auto flex items-center justify-center">
            <Empty
              description="Chọn một Part Number để xem lịch sử"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default PartNumberHistory;
